import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { ACCOUNT_QUERY_KEYS } from '@/lib/constants/account';
import { AccountApi } from '../AccountApi';
import type { CPUserCreateReq, CPUserUpdateReq, PasswordUpdateReq } from '@/lib/types/Auth';

export function useCreateUser() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (req: CPUserCreateReq) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.createUser(selectedCId, req);
    },
    onSuccess: () => {
      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.team(selectedCId) });
      }
    },
  });
}

export function useUpdateUser() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ cpuserId, req }: { cpuserId: number; req: CPUserUpdateReq }) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.updateUser(selectedCId, cpuserId, req);
    },
    onSuccess: () => {
      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.team(selectedCId) });
      }
    },
  });
}

export function useDeleteUser() {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cpuserId: number) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.deleteUser(selectedCId, cpuserId);
    },
    onSuccess: () => {
      if (selectedCId) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_QUERY_KEYS.team(selectedCId) });
      }
    },
  });
}

export function useUpdatePassword() {
  const { selectedCId } = useAuth();

  return useMutation({
    mutationFn: ({ cpuserId, req }: { cpuserId: number; req: PasswordUpdateReq }) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.updatePassword(selectedCId, cpuserId, req);
    },
  });
}

export function useCheckUsernameAvailable() {
  const { selectedCId } = useAuth();

  return useMutation({
    mutationFn: (username: string) => {
      if (!selectedCId) throw new Error('No account selected');
      return AccountApi.checkUsernameAvailable(selectedCId, username);
    },
  });
}
